import fabric from '@/lib/fabric';

export type DiagramId =
  // Flowchart
  | 'process'
  | 'decision'
  | 'terminator'
  | 'data'
  | 'document'
  | 'manual-input'
  | 'delay'
  | 'display'
  | 'predefined'
  | 'connector'
  | 'stored-data'
  | 'merge'
  | 'off-page'
  // UML
  | 'actor'
  | 'usecase'
  | 'class'
  | 'package'
  | 'note'
  | 'component'
  // Arrows
  | 'arrow-right'
  | 'arrow-left'
  | 'arrow-both'
  | 'chevron'
  | 'arrow-bent'
  | 'arrow-callout';

export interface DiagramStyle {
  fill: string;
  stroke: string;
  strokeWidth: number;
  label?: string;
}

type Pt = { x: number; y: number };

function poly(
  points: Pt[],
  style: DiagramStyle,
  extras?: Partial<fabric.IObjectOptions>
): fabric.Polygon {
  return new fabric.Polygon(points, {
    fill: style.fill,
    stroke: style.stroke,
    strokeWidth: style.strokeWidth,
    objectCaching: true,
    ...extras,
  });
}

function path(
  d: string,
  style: DiagramStyle,
  extras?: Partial<fabric.IObjectOptions>
): fabric.Path {
  return new fabric.Path(d, {
    fill: style.fill,
    stroke: style.stroke,
    strokeWidth: style.strokeWidth,
    objectCaching: true,
    ...extras,
  });
}

function labelText(
  text: string,
  w: number,
  h: number,
  fill = '#152033'
): fabric.Textbox {
  return new fabric.Textbox(text, {
    width: Math.max(40, w - 24),
    fontSize: Math.max(12, Math.min(18, h / 5)),
    fontFamily: 'Source Sans 3, Arial, sans-serif',
    fill,
    textAlign: 'center',
    originX: 'center',
    originY: 'center',
    left: w / 2,
    top: h / 2,
    splitByGrapheme: false,
  });
}

function withLabel(
  shape: fabric.Object,
  w: number,
  h: number,
  label: string | undefined,
  style: DiagramStyle
): fabric.Object {
  if (!label) {
    shape.set({ left: 0, top: 0, originX: 'left', originY: 'top' });
    return shape;
  }
  shape.set({ left: 0, top: 0, originX: 'left', originY: 'top' });
  const text = labelText(label, w, h);
  // Contrast: light text on dark fills
  const dark = /^#([0-4])/i.test(style.fill) || style.fill === '#152033';
  if (dark) text.set('fill', '#ffffff');
  return new fabric.Group([shape, text], {
    originX: 'left',
    originY: 'top',
  });
}

function diamond(w: number, h: number): Pt[] {
  return [
    { x: w / 2, y: 0 },
    { x: w, y: h / 2 },
    { x: w / 2, y: h },
    { x: 0, y: h / 2 },
  ];
}

function parallelogram(w: number, h: number, skew = 0.2): Pt[] {
  const s = w * skew;
  return [
    { x: s, y: 0 },
    { x: w, y: 0 },
    { x: w - s, y: h },
    { x: 0, y: h },
  ];
}

function trapezoid(w: number, h: number): Pt[] {
  const inset = w * 0.15;
  return [
    { x: inset, y: 0 },
    { x: w - inset, y: 0 },
    { x: w, y: h },
    { x: 0, y: h },
  ];
}

function chevronPoints(w: number, h: number): Pt[] {
  const tip = w * 0.22;
  return [
    { x: 0, y: 0 },
    { x: w - tip, y: 0 },
    { x: w, y: h / 2 },
    { x: w - tip, y: h },
    { x: 0, y: h },
    { x: tip, y: h / 2 },
  ];
}

function arrowRightPoints(w: number, h: number): Pt[] {
  const shaft = h * 0.35;
  const mid = h / 2;
  const head = w * 0.35;
  return [
    { x: 0, y: mid - shaft / 2 },
    { x: w - head, y: mid - shaft / 2 },
    { x: w - head, y: 0 },
    { x: w, y: mid },
    { x: w - head, y: h },
    { x: w - head, y: mid + shaft / 2 },
    { x: 0, y: mid + shaft / 2 },
  ];
}

function arrowBothPoints(w: number, h: number): Pt[] {
  const shaft = h * 0.35;
  const mid = h / 2;
  const head = w * 0.22;
  return [
    { x: 0, y: mid },
    { x: head, y: 0 },
    { x: head, y: mid - shaft / 2 },
    { x: w - head, y: mid - shaft / 2 },
    { x: w - head, y: 0 },
    { x: w, y: mid },
    { x: w - head, y: h },
    { x: w - head, y: mid + shaft / 2 },
    { x: head, y: mid + shaft / 2 },
    { x: head, y: h },
  ];
}

function calloutArrowPoints(w: number, h: number): Pt[] {
  const mid = h / 2;
  const head = w * 0.28;
  const notch = h * 0.22;
  return [
    { x: 0, y: notch },
    { x: w - head, y: notch },
    { x: w - head, y: 0 },
    { x: w, y: mid },
    { x: w - head, y: h },
    { x: w - head, y: h - notch },
    { x: 0, y: h - notch },
  ];
}

function offPagePoints(w: number, h: number): Pt[] {
  const tip = h * 0.28;
  return [
    { x: 0, y: 0 },
    { x: w, y: 0 },
    { x: w, y: h - tip },
    { x: w / 2, y: h },
    { x: 0, y: h - tip },
  ];
}

function mergePoints(w: number, h: number): Pt[] {
  return [
    { x: 0, y: 0 },
    { x: w, y: 0 },
    { x: w / 2, y: h },
  ];
}

function documentPath(w: number, h: number): string {
  const wave = h * 0.12;
  const body = h - wave;
  return [
    `M 0 0`,
    `L ${w} 0`,
    `L ${w} ${body}`,
    `Q ${w * 0.75} ${body + wave * 1.4} ${w / 2} ${body}`,
    `Q ${w * 0.25} ${body - wave * 0.4} 0 ${body}`,
    `Z`,
  ].join(' ');
}

function notePath(w: number, h: number): string {
  const fold = Math.min(w, h) * 0.22;
  return [
    `M 0 0`,
    `L ${w - fold} 0`,
    `L ${w} ${fold}`,
    `L ${w} ${h}`,
    `L 0 ${h}`,
    `Z`,
    `M ${w - fold} 0`,
    `L ${w - fold} ${fold}`,
    `L ${w} ${fold}`,
  ].join(' ');
}

function delayPath(w: number, h: number): string {
  const r = Math.min(w * 0.45, h / 2);
  return [
    `M 0 0`,
    `L ${w - r} 0`,
    `A ${r} ${r} 0 0 1 ${w - r} ${h}`,
    `L 0 ${h}`,
    `Z`,
  ].join(' ');
}

function displayPath(w: number, h: number): string {
  const curve = w * 0.18;
  return [
    `M ${curve} 0`,
    `L ${w - curve} 0`,
    `Q ${w} ${h / 2} ${w - curve} ${h}`,
    `L ${curve} ${h}`,
    `Q 0 ${h / 2} ${curve} 0`,
    `Z`,
  ].join(' ');
}

function storedDataPath(w: number, h: number): string {
  const curve = w * 0.18;
  return [
    `M ${curve} 0`,
    `L ${w} 0`,
    `Q ${w - curve} ${h / 2} ${w} ${h}`,
    `L ${curve} ${h}`,
    `Q 0 ${h / 2} ${curve} 0`,
    `Z`,
  ].join(' ');
}

function createActor(w: number, h: number, style: DiagramStyle): fabric.Group {
  const cx = w / 2;
  const headR = Math.min(w, h) * 0.14;
  const headY = headR + 2;
  const bodyTop = headY + headR + 4;
  const bodyBot = h * 0.62;
  const armY = bodyTop + (bodyBot - bodyTop) * 0.35;
  const legY = h - 4;
  const stroke = style.stroke || '#152033';
  const sw = Math.max(2, style.strokeWidth);

  const head = new fabric.Circle({
    left: cx - headR,
    top: headY - headR,
    radius: headR,
    fill: 'transparent',
    stroke,
    strokeWidth: sw,
  });
  const body = new fabric.Line([cx, bodyTop, cx, bodyBot], {
    stroke,
    strokeWidth: sw,
  });
  const arms = new fabric.Line([w * 0.15, armY, w * 0.85, armY], {
    stroke,
    strokeWidth: sw,
  });
  const legL = new fabric.Line([cx, bodyBot, w * 0.25, legY], {
    stroke,
    strokeWidth: sw,
  });
  const legR = new fabric.Line([cx, bodyBot, w * 0.75, legY], {
    stroke,
    strokeWidth: sw,
  });

  return new fabric.Group([head, body, arms, legL, legR], {
    originX: 'left',
    originY: 'top',
  });
}

function createClass(w: number, h: number, style: DiagramStyle): fabric.Group {
  const row = h / 3;
  const rect = new fabric.Rect({
    width: w,
    height: h,
    fill: style.fill,
    stroke: style.stroke,
    strokeWidth: style.strokeWidth,
    rx: 2,
    ry: 2,
  });
  const line1 = new fabric.Line([0, row, w, row], {
    stroke: style.stroke,
    strokeWidth: Math.max(1, style.strokeWidth),
  });
  const line2 = new fabric.Line([0, row * 2, w, row * 2], {
    stroke: style.stroke,
    strokeWidth: Math.max(1, style.strokeWidth),
  });
  const title = new fabric.Textbox('ClassName', {
    width: w - 12,
    left: 6,
    top: row / 2 - 10,
    fontSize: 14,
    fontWeight: 700,
    fontFamily: 'Source Sans 3, Arial, sans-serif',
    fill: '#152033',
    textAlign: 'center',
  });
  const attrs = new fabric.Textbox('+ attribute: Type', {
    width: w - 12,
    left: 6,
    top: row + 6,
    fontSize: 11,
    fontFamily: 'Source Sans 3, Arial, sans-serif',
    fill: '#45566e',
  });
  const methods = new fabric.Textbox('+ method()', {
    width: w - 12,
    left: 6,
    top: row * 2 + 6,
    fontSize: 11,
    fontFamily: 'Source Sans 3, Arial, sans-serif',
    fill: '#45566e',
  });
  return new fabric.Group([rect, line1, line2, title, attrs, methods], {
    originX: 'left',
    originY: 'top',
  });
}

function createPackage(w: number, h: number, style: DiagramStyle): fabric.Group {
  const tabW = w * 0.4;
  const tabH = h * 0.18;
  const tab = new fabric.Rect({
    width: tabW,
    height: tabH,
    fill: style.fill,
    stroke: style.stroke,
    strokeWidth: style.strokeWidth,
  });
  const body = new fabric.Rect({
    top: tabH - 1,
    width: w,
    height: h - tabH + 1,
    fill: style.fill,
    stroke: style.stroke,
    strokeWidth: style.strokeWidth,
  });
  const title = labelText('Package', w, h - tabH);
  title.set({ top: tabH + (h - tabH) / 2 });
  return new fabric.Group([tab, body, title], {
    originX: 'left',
    originY: 'top',
  });
}

function createComponent(w: number, h: number, style: DiagramStyle): fabric.Group {
  const rect = new fabric.Rect({
    left: w * 0.12,
    width: w * 0.88,
    height: h,
    fill: style.fill,
    stroke: style.stroke,
    strokeWidth: style.strokeWidth,
  });
  const tab1 = new fabric.Rect({
    left: 0,
    top: h * 0.22,
    width: w * 0.22,
    height: h * 0.16,
    fill: style.fill,
    stroke: style.stroke,
    strokeWidth: style.strokeWidth,
  });
  const tab2 = new fabric.Rect({
    left: 0,
    top: h * 0.55,
    width: w * 0.22,
    height: h * 0.16,
    fill: style.fill,
    stroke: style.stroke,
    strokeWidth: style.strokeWidth,
  });
  const title = labelText('Component', w * 0.88, h);
  title.set({ left: w * 0.12 + (w * 0.88) / 2 });
  return new fabric.Group([rect, tab1, tab2, title], {
    originX: 'left',
    originY: 'top',
  });
}

function createPredefined(w: number, h: number, style: DiagramStyle): fabric.Group {
  const inset = w * 0.1;
  const rect = new fabric.Rect({
    width: w,
    height: h,
    fill: style.fill,
    stroke: style.stroke,
    strokeWidth: style.strokeWidth,
  });
  const left = new fabric.Line([inset, 0, inset, h], {
    stroke: style.stroke,
    strokeWidth: Math.max(1, style.strokeWidth),
  });
  const right = new fabric.Line([w - inset, 0, w - inset, h], {
    stroke: style.stroke,
    strokeWidth: Math.max(1, style.strokeWidth),
  });
  return new fabric.Group([rect, left, right], {
    originX: 'left',
    originY: 'top',
  });
}

function createBentArrow(w: number, h: number, style: DiagramStyle): fabric.Group {
  const sw = Math.max(3, style.strokeWidth);
  const stroke = style.stroke || style.fill;
  const shaft = new fabric.Polyline(
    [
      { x: 8, y: 8 },
      { x: 8, y: h - 8 },
      { x: w - 28, y: h - 8 },
    ],
    {
      fill: 'transparent',
      stroke,
      strokeWidth: sw,
      strokeLineCap: 'round',
      strokeLineJoin: 'round',
    }
  );
  const head = poly(
    [
      { x: w - 32, y: h - 18 },
      { x: w - 4, y: h - 8 },
      { x: w - 32, y: h + 2 },
    ],
    { ...style, fill: stroke, stroke },
    { top: 0 }
  );
  return new fabric.Group([shaft, head], { originX: 'left', originY: 'top' });
}

/**
 * Build a CorelDRAW-style diagram shape sized to w×h (local 0,0).
 */
export function createDiagramShape(
  id: DiagramId,
  w: number,
  h: number,
  style: DiagramStyle
): fabric.Object {
  const paint = style;
  const label = style.label;

  switch (id) {
    case 'process':
      return withLabel(
        new fabric.Rect({
          width: w,
          height: h,
          rx: 6,
          ry: 6,
          fill: paint.fill,
          stroke: paint.stroke,
          strokeWidth: paint.strokeWidth,
        }),
        w,
        h,
        label ?? 'Process',
        paint
      );
    case 'decision':
      return withLabel(poly(diamond(w, h), paint), w, h, label ?? 'Decision?', paint);
    case 'terminator':
      return withLabel(
        new fabric.Rect({
          width: w,
          height: h,
          rx: h / 2,
          ry: h / 2,
          fill: paint.fill,
          stroke: paint.stroke,
          strokeWidth: paint.strokeWidth,
        }),
        w,
        h,
        label ?? 'Start / End',
        paint
      );
    case 'data':
      return withLabel(poly(parallelogram(w, h), paint), w, h, label ?? 'Data', paint);
    case 'document':
      return withLabel(path(documentPath(w, h), paint), w, h, label ?? 'Document', paint);
    case 'manual-input':
      return withLabel(poly(trapezoid(w, h), paint), w, h, label ?? 'Input', paint);
    case 'delay':
      return withLabel(path(delayPath(w, h), paint), w, h, label ?? 'Delay', paint);
    case 'display':
      return withLabel(path(displayPath(w, h), paint), w, h, label ?? 'Display', paint);
    case 'predefined': {
      const g = createPredefined(w, h, paint);
      const text = labelText(label ?? 'Subprocess', w, h);
      return new fabric.Group([g, text], { originX: 'left', originY: 'top' });
    }
    case 'connector':
      return withLabel(
        new fabric.Ellipse({
          rx: w / 2,
          ry: h / 2,
          fill: paint.fill,
          stroke: paint.stroke,
          strokeWidth: paint.strokeWidth,
        }),
        w,
        h,
        label ?? 'A',
        paint
      );
    case 'stored-data':
      return withLabel(path(storedDataPath(w, h), paint), w, h, label ?? 'Stored data', paint);
    case 'merge':
      return withLabel(poly(mergePoints(w, h), paint), w, h, label ?? 'Merge', paint);
    case 'off-page':
      return withLabel(poly(offPagePoints(w, h), paint), w, h, label ?? 'Off-page', paint);

    case 'actor':
      return createActor(w, h, paint);
    case 'usecase':
      return withLabel(
        new fabric.Ellipse({
          rx: w / 2,
          ry: h / 2,
          fill: paint.fill,
          stroke: paint.stroke,
          strokeWidth: paint.strokeWidth,
        }),
        w,
        h,
        label ?? 'Use case',
        paint
      );
    case 'class':
      return createClass(w, h, paint);
    case 'package':
      return createPackage(w, h, paint);
    case 'note':
      return withLabel(path(notePath(w, h), paint), w, h, label ?? 'Note', paint);
    case 'component':
      return createComponent(w, h, paint);

    case 'arrow-right':
      return poly(arrowRightPoints(w, h), paint);
    case 'arrow-left': {
      const pts = arrowRightPoints(w, h).map((p) => ({ x: w - p.x, y: p.y }));
      return poly(pts, paint);
    }
    case 'arrow-both':
      return poly(arrowBothPoints(w, h), paint);
    case 'chevron':
      return poly(chevronPoints(w, h), paint);
    case 'arrow-bent':
      return createBentArrow(w, h, paint);
    case 'arrow-callout':
      return withLabel(
        poly(calloutArrowPoints(w, h), paint),
        w,
        h,
        label ?? 'Callout',
        paint
      );
    default:
      return new fabric.Rect({
        width: w,
        height: h,
        fill: paint.fill,
        stroke: paint.stroke,
        strokeWidth: paint.strokeWidth,
      });
  }
}
